import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logger from "../../utils/logger";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { useAuth } from "../../contexts/AuthContext";
import ScreenTimeStep from "./steps/ScreenTimeStep";
import NameAgeStep from "./steps/NameAgeStep";
import ProfilePictureStep from "./steps/ProfilePictureStep";
import InterestsStep from "./steps/InterestsStep";
import DiscoverySourceStep from "./steps/DiscoverySourceStep";
import MeetStep from "./steps/MeetStep";
import AdventureStep from "./steps/AdventureStep";
import OnboardingProgress from "./OnboardingProgress";
import TrialWall from "../TrialWall";
import { FunnelSky, FunnelScreenStyles } from "../funnel/FunnelScreens";
import { OnboardingStyles } from "./OnboardingStyles";
import { stepVariants } from "./NOKit";

// iOS-mirrored order (NewOnboardingFlow, minus the role pre-step):
// screenTime → name+age → photo → interests → discovery ("how did you find
// us", kept for attribution) → meet → first adventure. Step 8 (not counted) is
// the in-flow mandatory paywall.
const STEPS = {
  1: ScreenTimeStep,
  2: NameAgeStep,
  3: ProfilePictureStep,
  4: InterestsStep,
  5: DiscoverySourceStep,
  6: MeetStep,
  7: AdventureStep,
};

/**
 * The website onboarding shell: the web funnel's look — its night sky, a white
 * card centred under a back button and segmented progress, steps that slide
 * in the direction of travel with their contents staggered — so a parent who
 * came through the funnel lands somewhere familiar. Each step renders its own
 * full-width CTA inside the card (no fixed footer: on a phone the keyboard and
 * the card never fight a bar for the screen).
 */
const OnboardingModal = () => {
  const { showOnboarding, currentStep, prevStep, isLoading } = useOnboarding();
  const { hasEntitlement, isTrialing, isGrandfathered } = useAuth();

  // Slide forward or back: compared with the step we were on last render
  const lastStep = useRef(currentStep);
  const direction = currentStep >= lastStep.current ? 1 : -1;
  useEffect(() => {
    lastStep.current = currentStep;
  }, [currentStep]);

  // The shell scrolls, not the body — a new step starts at its top
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [currentStep]);

  logger.log("🔵 OnboardingModal: Render check", { showOnboarding, currentStep });

  if (!showOnboarding) return null;

  // Step 8: the mandatory paywall, rendered as PART of the flow — the story
  // loader resolves into it with no modal pop, page load, or redirect.
  // Checkout navigates to Stripe; abandoning + reloading is caught by
  // Navigation's persisted CTA flag.
  if (currentStep === 8) {
    // Entitled while on the wall (gift redeemed, comp, grandfathered): the
    // mandatory paywall has nothing to sell — close out to the app instead.
    if (hasEntitlement || isTrialing || isGrandfathered) {
      try { localStorage.removeItem("pixley_show_subscription_cta"); } catch {}
      window.location.href = "/storyverse";
      return null;
    }
    return (
      <div
        className="fixed inset-0 z-50"
        style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}
      >
        <TrialWall isOpen onClose={() => {}} context={{ trigger: "post_onboarding", mandatory: true }} />
      </div>
    );
  }

  const Step = STEPS[currentStep] || ScreenTimeStep;
  // The reveal keeps its confetti; the last step gets the moon the funnel
  // raises for its finale (fetched a step early)
  const isReveal = currentStep === 6;
  const canGoBack = currentStep > 1 && !isLoading;

  return (
    <div className="ob-bg" ref={scrollRef} data-testid="onboarding">
      <FunnelScreenStyles />
      <OnboardingStyles />
      <FunnelSky scene={currentStep === 7 ? "moon" : "night"} moonSoon={currentStep === 6} />

      <div className="ob-shell">
        <motion.div
          className="ob-header"
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="ob-back-slot">
            <AnimatePresence>
              {canGoBack && (
                <motion.button
                  type="button"
                  className="ob-back"
                  onClick={prevStep}
                  aria-label="Back"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <OnboardingProgress />
        </motion.div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            className={`ob-card${isReveal ? " is-reveal" : ""}`}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <Step />
          </motion.div>
        </AnimatePresence>

        <p className="ob-wordmark" aria-hidden="true">PIXLEY</p>
      </div>
    </div>
  );
};

export default OnboardingModal;
